import { useCallback, useEffect, useState } from 'react';
import { Badge, Banner, BlockStack, Button, Card, DataTable, FormLayout, InlineStack, Link, Page, Text, TextField } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { deleteRequest, formatDate, getRequest } from '../../../../shared/components/functions';
import CompanySelect from '../../../../shared/components/companySelect';
import Toasts from '../../../../shared/components/toasts/toasts';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';

/*
  Settings > Hidden companies: companies that every user except full and data administrators never sees.
  The server enforces it (services/hiddenCompanies.js); this page only manages the list.
*/
export const SettingsHiddenCompaniesPage = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState({ available: true, message: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyLabel, setCompanyLabel] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState(false);
  const [errorToast, setErrorToast] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [statusData, list] = await Promise.all([getRequest('/api/hidden_companies/status'), getRequest('/api/hidden_companies')]);
    if (statusData && typeof statusData.available === 'boolean') setStatus(statusData);
    setItems(Array.isArray(list) ? list : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hideCompany = async () => {
    if (!companyId) {
      setError('Pick a company first.');
      return;
    }
    setError('');
    setSaving(true);
    const response = await fetch('/api/hidden_companies', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ FIRNR: companyId, Reason: reason })
    });
    setSaving(false);
    if (response.ok) {
      setToast(true);
      setCompanyId(null);
      setCompanyLabel('');
      setReason('');
      await load();
      return;
    }
    let message = 'The company could not be hidden.';
    try {
      const body = await response.json();
      if (body?.msg) message = body.msg;
    } catch (err) {
      // no JSON body
    }
    setError(message);
  };

  const unhideCompany = async (item) => {
    if (!window.confirm(`Make ${item.NAAM || `company ${item.FIRNR}`} visible again for every user?`)) return;
    setRemoving(item.FIRNR);
    const result = await deleteRequest(`/api/hidden_companies/${item.FIRNR}`);
    setRemoving(null);
    if (result) {
      setToast(true);
      await load();
    } else {
      setErrorToast(true);
    }
  };

  const relationBadges = (item) => (
    <InlineStack gap="100">
      {item.Klant ? <Badge tone="info">Customer</Badge> : null}
      {item.Leverancier ? <Badge tone="attention">Supplier</Badge> : null}
      {item.DISCONTINUED ? <Badge>Discontinued</Badge> : null}
    </InlineStack>
  );

  const rows = items.map((item) => [
    <Link removeUnderline url={`/companies/${item.FIRNR}`}>
      {item.NAAM || `Company ${item.FIRNR}`}
    </Link>,
    item.Klantnummer || '',
    [item.POSTNR, item.WOONPL].filter(Boolean).join(' '),
    relationBadges(item),
    item.Reason || '',
    item.created_by || '',
    item.CreatedAt ? formatDate(item.CreatedAt) : '',
    <Button size="slim" tone="critical" icon={DeleteIcon} loading={removing === item.FIRNR} onClick={() => unhideCompany(item)}>
      Unhide
    </Button>
  ]);

  return (
    <Page title="Hidden companies" backAction={{ content: 'Settings', url: '/settings' }}>
      <Toasts toast={toast} setToast={setToast} errorToast={errorToast} setErrorToast={setErrorToast} />
      <BlockStack gap="400">
        {!status.available ? (
          <Banner title="Hidden companies are not active in this database yet" tone="critical">
            <p>{status.message || 'Run server/sql/create_hidden_company_table.sql on this organisation database first.'}</p>
          </Banner>
        ) : null}

        <Banner tone="info">
          <p>
            A hidden company is invisible to every user except full and data administrators: the company itself, its contacts and all documents that mention it (incoming and outgoing invoices, credit
            notes, purchase and sales orders, quotations, inquiries, projects, jobs, machines, contracts, interactions, files and search results). For non-admin users these records behave as if they
            do not exist, so assign the approval of their invoices to an administrator.
          </p>
        </Banner>

        <Card>
          <CardHeading title="Hide a company" />
          <FormLayout>
            <CompanySelect
              label="Company"
              id="FIRNR"
              value={companyId}
              isClearable
              isDisabled={!status.available}
              onChange={(value, id, label) => {
                setCompanyId(value);
                setCompanyLabel(label || '');
                setError('');
              }}
            />
            <TextField label="Reason (only visible to administrators)" value={reason} onChange={setReason} maxLength={255} showCharacterCount autoComplete="off" disabled={!status.available} />
            {error ? (
              <Text as="p" tone="critical">
                {error}
              </Text>
            ) : null}
            <InlineStack align="end">
              <Button variant="primary" onClick={hideCompany} loading={saving} disabled={!status.available || !companyId}>
                {companyLabel ? `Hide ${companyLabel}` : 'Hide company'}
              </Button>
            </InlineStack>
          </FormLayout>
        </Card>

        <Card>
          <CardHeading title={`Hidden companies (${items.length})`} />
          {!loading && items.length === 0 ? (
            <Text as="p" tone="subdued">
              No companies are hidden. Every user sees every company.
            </Text>
          ) : (
            <DataTable
              stickyHeader
              increasedTableDensity
              verticalAlign="middle"
              columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
              headings={[<b>Company</b>, <b>Number</b>, <b>City</b>, <b>Relation</b>, <b>Reason</b>, <b>Hidden by</b>, <b>Since</b>, <b></b>]}
              rows={rows}
            />
          )}
        </Card>
      </BlockStack>
    </Page>
  );
};

export default SettingsHiddenCompaniesPage;
